/**
 * Per-item semantic validation of ONE formula-group payload — throws become
 * issues on this item only, and invalid appliesTo values are stripped so the
 * sanitized payload on the item is what assemble-accepted persists.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  collectFormulaIdentifiers,
  isSubformulaRefName,
  validateFormula,
} from '@trafficpulse/shared';

import type {
  FormulaGroupPayload,
  FormulaWeightPayload,
} from '@/features/flow-optimizer/clipboard/formula-clipboard.types';

import { checkWindowIssues } from './cross/date-window';
import type {
  LibraryValidationContext,
  ReviewIssue,
  ReviewItem,
} from './review-items';
import { finishItem } from './review-items';

export function validateFormulaGroup(
  payload: FormulaGroupPayload,
  weight: FormulaWeightPayload | undefined,
  index: number,
  ctx: LibraryValidationContext,
): ReviewItem {
  const issues: ReviewIssue[] = [];
  const sanitized = sanitizeAppliesTo(payload, issues);
  checkExpressions(sanitized, ctx, issues);
  checkSliders(sanitized, ctx, issues);
  checkDateRange(sanitized, ctx, issues);
  if (!sanitized.name?.trim()) {
    issues.push({ level: 'warning', message: 'Unnamed group.' });
  }
  return finishItem({
    id: `fg_${index}`,
    kind: 'formula-group',
    label: sanitized.name?.trim() || `Formula group ${index + 1}`,
    formulaGroup: sanitized,
    weight,
    issues,
  });
}

function sanitizeAppliesTo(
  payload: FormulaGroupPayload,
  issues: ReviewIssue[],
): FormulaGroupPayload {
  if (!payload.appliesTo) return payload;
  const known = payload.appliesTo.filter((layer) =>
    ['paths', 'landers', 'offers'].includes(layer),
  );
  if (known.length === payload.appliesTo.length) return payload;
  issues.push({ level: 'warning', message: 'Unknown appliesTo layers dropped.' });
  return { ...payload, appliesTo: known.length ? known : undefined };
}

function checkExpressions(
  payload: FormulaGroupPayload,
  ctx: LibraryValidationContext,
  issues: ReviewIssue[],
): void {
  if (payload.mode !== 'sliders' && !payload.expression?.trim()) {
    issues.push({
      level: 'warning',
      message: 'No expression — this group scores nothing until one is added.',
    });
  }
  checkOneExpression('expression', payload.expression ?? '', ctx, issues);
  for (const sub of payload.blocks?.subformulas ?? []) {
    const label = `sub-formula "${sub.name}"`;
    checkOneExpression(label, sub.expression ?? '', ctx, issues);
  }
}

function checkOneExpression(
  label: string,
  source: string,
  ctx: LibraryValidationContext,
  issues: ReviewIssue[],
): void {
  try {
    validateFormula(source);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    issues.push({ level: 'error', message: `Invalid ${label}: ${message}` });
    return;
  }
  const metricIds = ctx.metricIds;
  if (!metricIds?.size) return;
  const unknown = collectFormulaIdentifiers(source).filter(
    (name) => !isSubformulaRefName(name) && !metricIds.has(name),
  );
  if (unknown.length) {
    const plural = unknown.length > 1 ? 's' : '';
    issues.push({
      level: 'error',
      message: `Unknown metric${plural} in ${label}: ${unknown.join(', ')}.`,
    });
  }
}

function checkSliders(
  payload: FormulaGroupPayload,
  ctx: LibraryValidationContext,
  issues: ReviewIssue[],
): void {
  if (payload.mode !== 'sliders') return;
  const metrics = payload.sliders?.metrics ?? [];
  const metricIds = ctx.metricIds;
  const unknown = metricIds?.size
    ? metrics.map((m) => m.metric).filter((id) => !metricIds.has(id))
    : [];
  if (unknown.length) {
    issues.push({
      level: 'error',
      message: `Unknown slider metrics: ${unknown.join(', ')}.`,
    });
  }
  const sum = metrics.reduce((acc, m) => acc + (m.weight ?? 0), 0);
  if (metrics.length && Math.abs(sum - 100) > 1) {
    issues.push({
      level: 'warning',
      message: `Slider weights sum to ${sum}% — rebalanced to 100% on save.`,
    });
  }
}

function checkDateRange(
  payload: FormulaGroupPayload,
  ctx: LibraryValidationContext,
  issues: ReviewIssue[],
): void {
  checkWindowIssues(
    {
      dateRange: payload.dateRange,
      customFrom: payload.dateRangeCustomFrom,
      customTo: payload.dateRangeCustomTo,
    },
    ctx,
    issues,
  );
}
